import * as z from 'zod';

export const ListEventsByContactIdRequest = z.object({
  contactId: z
    .string()
    .describe(
      'ID of the [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nto retrieve events for. Required, unless you provide `cursorPaging`.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fromLocalDate: z
        .string()
        .describe(
          "Local start date and time from which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.\n\nEvents that start before the `fromLocalDate` but end after it are included in\nthe results. Must be earlier than `toLocalDate` unless the sort order is descending."
        )
        .optional()
        .nullable(),
      toLocalDate: z
        .string()
        .describe(
          "Local end date and time up to which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`. Can't be more than a full year after\n`2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.\n\nEvents that start before `toLocalDate` but end after it are included in the\nresults. Must be later than `fromLocalDate` unless the sort order is descending."
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and\n`adjustedEnd`. For example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          '[App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of events to return.\nDefaults to `50`. Maximum `100`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('Cursor to retrieve the next page of the results.')
        .optional(),
      sort: z
        .object({
          fieldName: z
            .string()
            .describe(
              'The field to sort by.\nEither `start` or `end`.\nDefault is `start`.'
            )
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe(
          'Sorting for the results.\nIf not specified, results are sorted by `start` ascending.\nFor DESC order, events are sorted by `end` time in descending order.'
        )
        .optional(),
    })
    .optional(),
});
export const ListEventsByContactIdResponse = z.object({
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .min(36)
          .max(250)
          .optional()
          .nullable(),
        scheduleId: z
          .string()
          .describe(
            "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalScheduleId: z
          .string()
          .describe(
            'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        scheduleName: z
          .string()
          .describe('Schedule name.')
          .optional()
          .nullable(),
        type: z
          .string()
          .describe(
            'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['CONFIRMED', 'CANCELLED'])
          .describe(
            'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
          )
          .optional(),
        title: z
          .string()
          .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        start: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe('Information about when the event starts.')
          .optional(),
        end: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
          )
          .optional(),
        adjustedStart: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        adjustedEnd: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
          )
          .optional()
          .nullable(),
        recurrenceType: z
          .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
          .describe(
            "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
          )
          .optional(),
        recurrenceRule: z
          .object({
            frequency: z
              .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
              .describe(
                "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
              )
              .optional(),
            interval: z
              .number()
              .int()
              .describe(
                'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
              )
              .min(1)
              .max(4)
              .optional()
              .nullable(),
            days: z
              .array(
                z.enum([
                  'MONDAY',
                  'TUESDAY',
                  'WEDNESDAY',
                  'THURSDAY',
                  'FRIDAY',
                  'SATURDAY',
                  'SUNDAY',
                ])
              )
              .min(1)
              .max(1)
              .optional(),
            until: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Date until when the event repeats.\nIf not specified, the event repeats forever.'
              )
              .optional(),
            adjustedUntil: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
          })
          .describe(
            "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
          )
          .optional(),
        recurringEventId: z
          .string()
          .describe(
            'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
          )
          .min(64)
          .max(64)
          .optional()
          .nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
          )
          .optional(),
        location: z
          .object({
            _id: z
              .string()
              .describe(
                '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
              .describe(
                "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
              )
              .optional(),
            name: z
              .string()
              .describe(
                "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
            address: z
              .string()
              .describe(
                "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
              )
              .min(1)
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .optional(),
        resources: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Resource ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z.string().describe('Resource name.').optional().nullable(),
              type: z.string().describe('Resource type.').optional().nullable(),
              transparency: z
                .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                .describe(
                  "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                )
                .optional(),
              permissionRole: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
        totalCapacity: z
          .number()
          .int()
          .describe(
            'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .min(0)
          .optional()
          .nullable(),
        remainingCapacity: z
          .number()
          .int()
          .describe(
            'Number of participants who can still be added to the event.'
          )
          .optional()
          .nullable(),
        participants: z
          .object({
            total: z
              .number()
              .int()
              .describe('Total number of participants.\n\nMin: `0`')
              .min(0)
              .optional()
              .nullable(),
            list: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                    )
                    .min(1)
                    .max(200)
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Participant's phone number.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Participant's email address.")
                    .email()
                    .optional()
                    .nullable(),
                  contactId: z
                    .string()
                    .describe(
                      '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            hasMore: z
              .boolean()
              .describe(
                "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
              .describe(
                "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
              )
              .optional(),
          })
          .describe(
            "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
          )
          .optional(),
        conferencingDetails: z
          .object({
            type: z
              .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
              .describe('Conference type. For example, `Zoom`.')
              .optional(),
            hostUrl: z
              .string()
              .describe(
                'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            guestUrl: z
              .string()
              .describe(
                'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            password: z
              .string()
              .describe(
                'Conference password.\n\nMin: 1 character\nMax: 100 characters'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            externalId: z
              .string()
              .describe(
                'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
          })
          .describe(
            "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
          )
          .optional(),
        notes: z
          .string()
          .describe(
            'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
          )
          .min(1)
          .max(5000)
          .optional()
          .nullable(),
        inheritedFields: z
          .array(
            z.enum([
              'TITLE',
              'TIME_ZONE',
              'TIME',
              'LOCATION',
              'RESOURCES',
              'CAPACITY',
              'PARTICIPANTS',
              'CONFERENCING_DETAILS',
            ])
          )
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        permissions: z
          .array(
            z.object({
              identity: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ wixUserId: z.never().optional() }),
                    z.object({
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                )
                .optional(),
              role: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                )
                .optional(),
            })
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling applications or users to save custom data related to the event.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe(
          'Use these cursor to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const ListEventsByMemberIdRequest = z.object({
  memberId: z
    .string()
    .describe(
      "ID of the [member](https://dev.wix.com/docs/rest/crm/members-contacts/members/members/member-object)\nto retrieve events for. Required, unless you provide `cursorPaging`.\nProvide `me` to retrieve events for the currently logged-in member.\nYou must have the `Read Calendars - Including PI`\n[permission scope](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/configure-permissions-for-your-app)\nto retrieve events for members who aren't the currently logged in member."
    )
    .min(2)
    .max(36),
  options: z
    .object({
      fromLocalDate: z
        .string()
        .describe(
          'Local start date and time from which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.\n\nEvents that start before the `fromLocalDate` but end after it are included in\nthe results. Must be earlier than `toLocalDate` unless the sort order is descending.'
        )
        .optional()
        .nullable(),
      toLocalDate: z
        .string()
        .describe(
          "Local end date and time up to which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`. Can't be more than a full year after\n`fromLocalDate`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.\n\nEvents that start before `toLocalDate` but end after it are included in the\nresults. Must be later than `fromLocalDate` unless the sort order is descending."
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and\n`adjustedEnd`. For example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          '[App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of events to return.\nDefaults to `50`. Maximum `100`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('Cursor to retrieve the next page of the results.')
        .optional(),
      eventIds: z.array(z.string()).max(100).optional(),
      sort: z
        .object({
          fieldName: z
            .string()
            .describe(
              'The field to sort by.\nEither `start` or `end`.\nDefault is `start`.'
            )
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe(
          'Sorting for the results.\nIf not specified, results are sorted by `start` ascending.\nFor DESC order, events are sorted by `end` time in descending order.'
        )
        .optional(),
    })
    .optional(),
});
export const ListEventsByMemberIdResponse = z.object({
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .min(36)
          .max(250)
          .optional()
          .nullable(),
        scheduleId: z
          .string()
          .describe(
            "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalScheduleId: z
          .string()
          .describe(
            'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        scheduleName: z
          .string()
          .describe('Schedule name.')
          .optional()
          .nullable(),
        type: z
          .string()
          .describe(
            'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['CONFIRMED', 'CANCELLED'])
          .describe(
            'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
          )
          .optional(),
        title: z
          .string()
          .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        start: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe('Information about when the event starts.')
          .optional(),
        end: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
          )
          .optional(),
        adjustedStart: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        adjustedEnd: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
          )
          .optional()
          .nullable(),
        recurrenceType: z
          .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
          .describe(
            "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
          )
          .optional(),
        recurrenceRule: z
          .object({
            frequency: z
              .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
              .describe(
                "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
              )
              .optional(),
            interval: z
              .number()
              .int()
              .describe(
                'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
              )
              .min(1)
              .max(4)
              .optional()
              .nullable(),
            days: z
              .array(
                z.enum([
                  'MONDAY',
                  'TUESDAY',
                  'WEDNESDAY',
                  'THURSDAY',
                  'FRIDAY',
                  'SATURDAY',
                  'SUNDAY',
                ])
              )
              .min(1)
              .max(1)
              .optional(),
            until: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Date until when the event repeats.\nIf not specified, the event repeats forever.'
              )
              .optional(),
            adjustedUntil: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
          })
          .describe(
            "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
          )
          .optional(),
        recurringEventId: z
          .string()
          .describe(
            'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
          )
          .min(64)
          .max(64)
          .optional()
          .nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
          )
          .optional(),
        location: z
          .object({
            _id: z
              .string()
              .describe(
                '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
              .describe(
                "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
              )
              .optional(),
            name: z
              .string()
              .describe(
                "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
            address: z
              .string()
              .describe(
                "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
              )
              .min(1)
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .optional(),
        resources: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Resource ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z.string().describe('Resource name.').optional().nullable(),
              type: z.string().describe('Resource type.').optional().nullable(),
              transparency: z
                .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                .describe(
                  "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                )
                .optional(),
              permissionRole: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
        totalCapacity: z
          .number()
          .int()
          .describe(
            'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .min(0)
          .optional()
          .nullable(),
        remainingCapacity: z
          .number()
          .int()
          .describe(
            'Number of participants who can still be added to the event.'
          )
          .optional()
          .nullable(),
        participants: z
          .object({
            total: z
              .number()
              .int()
              .describe('Total number of participants.\n\nMin: `0`')
              .min(0)
              .optional()
              .nullable(),
            list: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                    )
                    .min(1)
                    .max(200)
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Participant's phone number.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Participant's email address.")
                    .email()
                    .optional()
                    .nullable(),
                  contactId: z
                    .string()
                    .describe(
                      '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            hasMore: z
              .boolean()
              .describe(
                "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
              .describe(
                "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
              )
              .optional(),
          })
          .describe(
            "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
          )
          .optional(),
        conferencingDetails: z
          .object({
            type: z
              .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
              .describe('Conference type. For example, `Zoom`.')
              .optional(),
            hostUrl: z
              .string()
              .describe(
                'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            guestUrl: z
              .string()
              .describe(
                'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            password: z
              .string()
              .describe(
                'Conference password.\n\nMin: 1 character\nMax: 100 characters'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            externalId: z
              .string()
              .describe(
                'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
          })
          .describe(
            "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
          )
          .optional(),
        notes: z
          .string()
          .describe(
            'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
          )
          .min(1)
          .max(5000)
          .optional()
          .nullable(),
        inheritedFields: z
          .array(
            z.enum([
              'TITLE',
              'TIME_ZONE',
              'TIME',
              'LOCATION',
              'RESOURCES',
              'CAPACITY',
              'PARTICIPANTS',
              'CONFERENCING_DETAILS',
            ])
          )
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        permissions: z
          .array(
            z.object({
              identity: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ wixUserId: z.never().optional() }),
                    z.object({
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                )
                .optional(),
              role: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                )
                .optional(),
            })
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling applications or users to save custom data related to the event.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe(
          'Use these cursor to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const GetEventRequest = z.object({
  eventId: z
    .string()
    .describe(
      'ID of the event to retrieve.\n\nMin: 36 characters\nMax: 250 characters'
    )
    .min(36)
    .max(250),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      fields: z
        .array(z.enum(['PI_FIELDS', 'OWN_PI_FIELDS']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const GetEventResponse = z.object({
  _id: z.string().describe('Event ID.').min(36).max(250).optional().nullable(),
  scheduleId: z
    .string()
    .describe(
      "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  externalScheduleId: z
    .string()
    .describe(
      'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  scheduleName: z.string().describe('Schedule name.').optional().nullable(),
  type: z
    .string()
    .describe(
      'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['CONFIRMED', 'CANCELLED'])
    .describe(
      'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
    )
    .optional(),
  title: z
    .string()
    .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  start: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe('Information about when the event starts.')
    .optional(),
  end: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
    )
    .optional(),
  adjustedStart: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  adjustedEnd: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  timeZone: z
    .string()
    .describe(
      'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
    )
    .optional()
    .nullable(),
  recurrenceType: z
    .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
    .describe(
      "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
    )
    .optional(),
  recurrenceRule: z
    .object({
      frequency: z
        .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
        .describe(
          "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
        )
        .optional(),
      interval: z
        .number()
        .int()
        .describe(
          'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
        )
        .min(1)
        .max(4)
        .optional()
        .nullable(),
      days: z
        .array(
          z.enum([
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY',
          ])
        )
        .min(1)
        .max(1)
        .optional(),
      until: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Date until when the event repeats.\nIf not specified, the event repeats forever.'
        )
        .optional(),
      adjustedUntil: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
    })
    .describe(
      "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
    )
    .optional(),
  recurringEventId: z
    .string()
    .describe(
      'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
    )
    .min(64)
    .max(64)
    .optional()
    .nullable(),
  transparency: z
    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
    .describe(
      'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
    )
    .optional(),
  location: z
    .object({
      _id: z
        .string()
        .describe(
          '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
        .describe(
          "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
        )
        .optional(),
      name: z
        .string()
        .describe(
          "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
      address: z
        .string()
        .describe(
          "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
        )
        .min(1)
        .max(300)
        .optional()
        .nullable(),
    })
    .describe(
      'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .optional(),
  resources: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Resource ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Resource name.').optional().nullable(),
        type: z.string().describe('Resource type.').optional().nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
          )
          .optional(),
        permissionRole: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
          )
          .optional(),
      })
    )
    .max(100)
    .optional(),
  totalCapacity: z
    .number()
    .int()
    .describe(
      'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .min(0)
    .optional()
    .nullable(),
  remainingCapacity: z
    .number()
    .int()
    .describe('Number of participants who can still be added to the event.')
    .optional()
    .nullable(),
  participants: z
    .object({
      total: z
        .number()
        .int()
        .describe('Total number of participants.\n\nMin: `0`')
        .min(0)
        .optional()
        .nullable(),
      list: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                'Participant name.\n\nMin: 1 character\nMax: 200 characters'
              )
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Participant's phone number.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Participant's email address.")
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe(
                '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      hasMore: z
        .boolean()
        .describe(
          "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
        .describe(
          "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
        )
        .optional(),
    })
    .describe(
      "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
    )
    .optional(),
  conferencingDetails: z
    .object({
      type: z
        .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
        .describe('Conference type. For example, `Zoom`.')
        .optional(),
      hostUrl: z
        .string()
        .describe(
          'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      guestUrl: z
        .string()
        .describe(
          'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      password: z
        .string()
        .describe(
          'Conference password.\n\nMin: 1 character\nMax: 100 characters'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      externalId: z
        .string()
        .describe(
          'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
    })
    .describe(
      "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
    )
    .optional(),
  notes: z
    .string()
    .describe(
      'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
    )
    .min(1)
    .max(5000)
    .optional()
    .nullable(),
  inheritedFields: z
    .array(
      z.enum([
        'TITLE',
        'TIME_ZONE',
        'TIME',
        'LOCATION',
        'RESOURCES',
        'CAPACITY',
        'PARTICIPANTS',
        'CONFERENCING_DETAILS',
      ])
    )
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  permissions: z
    .array(
      z.object({
        identity: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ wixUserId: z.never().optional() }),
              z.object({
                wixUserId: z
                  .string()
                  .describe(
                    'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
          )
          .optional(),
        role: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
          )
          .optional(),
      })
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling applications or users to save custom data related to the event.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
});
export const ListEventsRequest = z.object({
  eventIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      fields: z
        .array(z.enum(['PI_FIELDS', 'OWN_PI_FIELDS']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const ListEventsResponse = z.object({
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .min(36)
          .max(250)
          .optional()
          .nullable(),
        scheduleId: z
          .string()
          .describe(
            "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalScheduleId: z
          .string()
          .describe(
            'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        scheduleName: z
          .string()
          .describe('Schedule name.')
          .optional()
          .nullable(),
        type: z
          .string()
          .describe(
            'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['CONFIRMED', 'CANCELLED'])
          .describe(
            'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
          )
          .optional(),
        title: z
          .string()
          .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        start: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe('Information about when the event starts.')
          .optional(),
        end: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
          )
          .optional(),
        adjustedStart: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        adjustedEnd: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
          )
          .optional()
          .nullable(),
        recurrenceType: z
          .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
          .describe(
            "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
          )
          .optional(),
        recurrenceRule: z
          .object({
            frequency: z
              .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
              .describe(
                "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
              )
              .optional(),
            interval: z
              .number()
              .int()
              .describe(
                'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
              )
              .min(1)
              .max(4)
              .optional()
              .nullable(),
            days: z
              .array(
                z.enum([
                  'MONDAY',
                  'TUESDAY',
                  'WEDNESDAY',
                  'THURSDAY',
                  'FRIDAY',
                  'SATURDAY',
                  'SUNDAY',
                ])
              )
              .min(1)
              .max(1)
              .optional(),
            until: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Date until when the event repeats.\nIf not specified, the event repeats forever.'
              )
              .optional(),
            adjustedUntil: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
          })
          .describe(
            "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
          )
          .optional(),
        recurringEventId: z
          .string()
          .describe(
            'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
          )
          .min(64)
          .max(64)
          .optional()
          .nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
          )
          .optional(),
        location: z
          .object({
            _id: z
              .string()
              .describe(
                '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
              .describe(
                "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
              )
              .optional(),
            name: z
              .string()
              .describe(
                "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
            address: z
              .string()
              .describe(
                "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
              )
              .min(1)
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .optional(),
        resources: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Resource ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z.string().describe('Resource name.').optional().nullable(),
              type: z.string().describe('Resource type.').optional().nullable(),
              transparency: z
                .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                .describe(
                  "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                )
                .optional(),
              permissionRole: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
        totalCapacity: z
          .number()
          .int()
          .describe(
            'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .min(0)
          .optional()
          .nullable(),
        remainingCapacity: z
          .number()
          .int()
          .describe(
            'Number of participants who can still be added to the event.'
          )
          .optional()
          .nullable(),
        participants: z
          .object({
            total: z
              .number()
              .int()
              .describe('Total number of participants.\n\nMin: `0`')
              .min(0)
              .optional()
              .nullable(),
            list: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                    )
                    .min(1)
                    .max(200)
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Participant's phone number.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Participant's email address.")
                    .email()
                    .optional()
                    .nullable(),
                  contactId: z
                    .string()
                    .describe(
                      '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            hasMore: z
              .boolean()
              .describe(
                "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
              .describe(
                "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
              )
              .optional(),
          })
          .describe(
            "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
          )
          .optional(),
        conferencingDetails: z
          .object({
            type: z
              .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
              .describe('Conference type. For example, `Zoom`.')
              .optional(),
            hostUrl: z
              .string()
              .describe(
                'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            guestUrl: z
              .string()
              .describe(
                'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            password: z
              .string()
              .describe(
                'Conference password.\n\nMin: 1 character\nMax: 100 characters'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            externalId: z
              .string()
              .describe(
                'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
          })
          .describe(
            "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
          )
          .optional(),
        notes: z
          .string()
          .describe(
            'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
          )
          .min(1)
          .max(5000)
          .optional()
          .nullable(),
        inheritedFields: z
          .array(
            z.enum([
              'TITLE',
              'TIME_ZONE',
              'TIME',
              'LOCATION',
              'RESOURCES',
              'CAPACITY',
              'PARTICIPANTS',
              'CONFERENCING_DETAILS',
            ])
          )
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        permissions: z
          .array(
            z.object({
              identity: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ wixUserId: z.never().optional() }),
                    z.object({
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                )
                .optional(),
              role: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                )
                .optional(),
            })
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling applications or users to save custom data related to the event.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const QueryEventsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          transparency: z
            .union([
              z.string(),
              z.object({ $eq: z.string() }).partial().strict(),
            ])
            .optional(),
          appId: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          scheduleId: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          externalScheduleId: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          type: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          recurringEventId: z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          'location._id': z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          'location.type': z
            .object({ $eq: z.string(), $in: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          remainingCapacity: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          'participants.total': z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          totalCapacity: z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          'resources._id': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'resources.type': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'resources.transparency': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          location: z
            .object({ $exists: z.boolean() })
            .partial()
            .strict()
            .optional(),
          conferencingDetails: z
            .object({ $exists: z.boolean() })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z.array(z.object({})).optional(),
    })
    .catchall(z.any())
    .describe(
      'Query containing filters and paging.\nDo not specify filters for start and end dates inside `query.filter`, instead\nspecify `fromLocalDate` and `toLocalDate`.'
    ),
  options: z
    .object({
      fromLocalDate: z
        .string()
        .describe(
          'Local start date and time from which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`.\n\nEvents that start before the `fromLocalDate` but end after it are included in\nthe results. Must be earlier than `toLocalDate` unless the sort order is\ndescending.'
        )
        .optional()
        .nullable(),
      toLocalDate: z
        .string()
        .describe(
          'Local end date and time up to which events are returned in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2024-01-30T13:30:00`.\n\nEvents that start before `toLocalDate` but end after it are included in the\nresults. Must be later than `fromLocalDate` unless the sort order is\ndescending.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and\n`adjustedEnd`. For example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .array(z.enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION']))
        .max(5)
        .optional(),
      fields: z
        .array(z.enum(['PI_FIELDS', 'OWN_PI_FIELDS']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const QueryEventsResponse = z.object({
  events: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Event ID.')
          .min(36)
          .max(250)
          .optional()
          .nullable(),
        scheduleId: z
          .string()
          .describe(
            "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalScheduleId: z
          .string()
          .describe(
            'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        scheduleName: z
          .string()
          .describe('Schedule name.')
          .optional()
          .nullable(),
        type: z
          .string()
          .describe(
            'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['CONFIRMED', 'CANCELLED'])
          .describe(
            'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
          )
          .optional(),
        title: z
          .string()
          .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        start: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe('Information about when the event starts.')
          .optional(),
        end: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
          )
          .optional(),
        adjustedStart: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        adjustedEnd: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
          )
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
          )
          .optional()
          .nullable(),
        recurrenceType: z
          .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
          .describe(
            "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
          )
          .optional(),
        recurrenceRule: z
          .object({
            frequency: z
              .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
              .describe(
                "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
              )
              .optional(),
            interval: z
              .number()
              .int()
              .describe(
                'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
              )
              .min(1)
              .max(4)
              .optional()
              .nullable(),
            days: z
              .array(
                z.enum([
                  'MONDAY',
                  'TUESDAY',
                  'WEDNESDAY',
                  'THURSDAY',
                  'FRIDAY',
                  'SATURDAY',
                  'SUNDAY',
                ])
              )
              .min(1)
              .max(1)
              .optional(),
            until: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Date until when the event repeats.\nIf not specified, the event repeats forever.'
              )
              .optional(),
            adjustedUntil: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
          })
          .describe(
            "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
          )
          .optional(),
        recurringEventId: z
          .string()
          .describe(
            'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
          )
          .min(64)
          .max(64)
          .optional()
          .nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
          )
          .optional(),
        location: z
          .object({
            _id: z
              .string()
              .describe(
                '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
              .describe(
                "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
              )
              .optional(),
            name: z
              .string()
              .describe(
                "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
            address: z
              .string()
              .describe(
                "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
              )
              .min(1)
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .optional(),
        resources: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Resource ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z.string().describe('Resource name.').optional().nullable(),
              type: z.string().describe('Resource type.').optional().nullable(),
              transparency: z
                .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                .describe(
                  "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                )
                .optional(),
              permissionRole: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
        totalCapacity: z
          .number()
          .int()
          .describe(
            'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
          )
          .min(0)
          .optional()
          .nullable(),
        remainingCapacity: z
          .number()
          .int()
          .describe(
            'Number of participants who can still be added to the event.'
          )
          .optional()
          .nullable(),
        participants: z
          .object({
            total: z
              .number()
              .int()
              .describe('Total number of participants.\n\nMin: `0`')
              .min(0)
              .optional()
              .nullable(),
            list: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                    )
                    .min(1)
                    .max(200)
                    .optional()
                    .nullable(),
                  phone: z
                    .string()
                    .describe("Participant's phone number.")
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Participant's email address.")
                    .email()
                    .optional()
                    .nullable(),
                  contactId: z
                    .string()
                    .describe(
                      '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            hasMore: z
              .boolean()
              .describe(
                "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
              .describe(
                "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
              )
              .optional(),
          })
          .describe(
            "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
          )
          .optional(),
        conferencingDetails: z
          .object({
            type: z
              .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
              .describe('Conference type. For example, `Zoom`.')
              .optional(),
            hostUrl: z
              .string()
              .describe(
                'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            guestUrl: z
              .string()
              .describe(
                'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
              )
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            password: z
              .string()
              .describe(
                'Conference password.\n\nMin: 1 character\nMax: 100 characters'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            externalId: z
              .string()
              .describe(
                'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
              )
              .min(1)
              .max(150)
              .optional()
              .nullable(),
          })
          .describe(
            "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
          )
          .optional(),
        notes: z
          .string()
          .describe(
            'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
          )
          .min(1)
          .max(5000)
          .optional()
          .nullable(),
        inheritedFields: z
          .array(
            z.enum([
              'TITLE',
              'TIME_ZONE',
              'TIME',
              'LOCATION',
              'RESOURCES',
              'CAPACITY',
              'PARTICIPANTS',
              'CONFERENCING_DETAILS',
            ])
          )
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        permissions: z
          .array(
            z.object({
              identity: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ wixUserId: z.never().optional() }),
                    z.object({
                      wixUserId: z
                        .string()
                        .describe(
                          'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
                .describe(
                  '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                )
                .optional(),
              role: z
                .enum(['WRITER', 'COMMENTER'])
                .describe(
                  'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                )
                .optional(),
            })
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling applications or users to save custom data related to the event.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
        })
        .describe(
          'Use these cursor to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const CreateEventRequest = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z.enum(['CONFIRMED', 'CANCELLED']).optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.'),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        ),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z.enum(['UNKNOWN_FREQUENCY', 'WEEKLY']),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                ),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                ),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z.enum(['BUSINESS', 'CUSTOMER', 'CUSTOM']),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .optional(),
            permissionRole: z.enum(['WRITER', 'COMMENTER']).optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z.enum(['CONFIRMED', 'PENDING_CONFIRMATION']).optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z.enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM']).optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z.enum(['WRITER', 'COMMENTER']).optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Event to create.'),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
      idempotencyKey: z
        .string()
        .describe(
          "Idempotency key guaranteeing that you don't create the same event more than once.\nMust be a valid UUID/GUID, for example `550e8400-e29b-41d4-a716-446655440000`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CreateEventResponse = z.object({
  _id: z.string().describe('Event ID.').min(36).max(250).optional().nullable(),
  scheduleId: z
    .string()
    .describe(
      "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  externalScheduleId: z
    .string()
    .describe(
      'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  scheduleName: z.string().describe('Schedule name.').optional().nullable(),
  type: z
    .string()
    .describe(
      'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['CONFIRMED', 'CANCELLED'])
    .describe(
      'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
    )
    .optional(),
  title: z
    .string()
    .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  start: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe('Information about when the event starts.')
    .optional(),
  end: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
    )
    .optional(),
  adjustedStart: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  adjustedEnd: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  timeZone: z
    .string()
    .describe(
      'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
    )
    .optional()
    .nullable(),
  recurrenceType: z
    .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
    .describe(
      "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
    )
    .optional(),
  recurrenceRule: z
    .object({
      frequency: z
        .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
        .describe(
          "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
        )
        .optional(),
      interval: z
        .number()
        .int()
        .describe(
          'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
        )
        .min(1)
        .max(4)
        .optional()
        .nullable(),
      days: z
        .array(
          z.enum([
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY',
          ])
        )
        .min(1)
        .max(1)
        .optional(),
      until: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Date until when the event repeats.\nIf not specified, the event repeats forever.'
        )
        .optional(),
      adjustedUntil: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
    })
    .describe(
      "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
    )
    .optional(),
  recurringEventId: z
    .string()
    .describe(
      'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
    )
    .min(64)
    .max(64)
    .optional()
    .nullable(),
  transparency: z
    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
    .describe(
      'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
    )
    .optional(),
  location: z
    .object({
      _id: z
        .string()
        .describe(
          '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
        .describe(
          "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
        )
        .optional(),
      name: z
        .string()
        .describe(
          "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
      address: z
        .string()
        .describe(
          "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
        )
        .min(1)
        .max(300)
        .optional()
        .nullable(),
    })
    .describe(
      'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .optional(),
  resources: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Resource ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Resource name.').optional().nullable(),
        type: z.string().describe('Resource type.').optional().nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
          )
          .optional(),
        permissionRole: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
          )
          .optional(),
      })
    )
    .max(100)
    .optional(),
  totalCapacity: z
    .number()
    .int()
    .describe(
      'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .min(0)
    .optional()
    .nullable(),
  remainingCapacity: z
    .number()
    .int()
    .describe('Number of participants who can still be added to the event.')
    .optional()
    .nullable(),
  participants: z
    .object({
      total: z
        .number()
        .int()
        .describe('Total number of participants.\n\nMin: `0`')
        .min(0)
        .optional()
        .nullable(),
      list: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                'Participant name.\n\nMin: 1 character\nMax: 200 characters'
              )
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Participant's phone number.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Participant's email address.")
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe(
                '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      hasMore: z
        .boolean()
        .describe(
          "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
        .describe(
          "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
        )
        .optional(),
    })
    .describe(
      "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
    )
    .optional(),
  conferencingDetails: z
    .object({
      type: z
        .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
        .describe('Conference type. For example, `Zoom`.')
        .optional(),
      hostUrl: z
        .string()
        .describe(
          'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      guestUrl: z
        .string()
        .describe(
          'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      password: z
        .string()
        .describe(
          'Conference password.\n\nMin: 1 character\nMax: 100 characters'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      externalId: z
        .string()
        .describe(
          'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
    })
    .describe(
      "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
    )
    .optional(),
  notes: z
    .string()
    .describe(
      'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
    )
    .min(1)
    .max(5000)
    .optional()
    .nullable(),
  inheritedFields: z
    .array(
      z.enum([
        'TITLE',
        'TIME_ZONE',
        'TIME',
        'LOCATION',
        'RESOURCES',
        'CAPACITY',
        'PARTICIPANTS',
        'CONFERENCING_DETAILS',
      ])
    )
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  permissions: z
    .array(
      z.object({
        identity: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ wixUserId: z.never().optional() }),
              z.object({
                wixUserId: z
                  .string()
                  .describe(
                    'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
          )
          .optional(),
        role: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
          )
          .optional(),
      })
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling applications or users to save custom data related to the event.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
});
export const BulkCreateEventRequest = z.object({
  events: z
    .array(
      z.object({
        event: z
          .object({
            _id: z
              .string()
              .describe('Event ID.')
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            scheduleId: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            externalScheduleId: z
              .string()
              .describe(
                'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            scheduleName: z
              .string()
              .describe('Schedule name.')
              .optional()
              .nullable(),
            type: z
              .string()
              .describe(
                'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
              )
              .optional()
              .nullable(),
            status: z.enum(['CONFIRMED', 'CANCELLED']).optional(),
            title: z
              .string()
              .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            start: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information about when the event starts.'),
            end: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
              ),
            adjustedStart: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            adjustedEnd: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
              )
              .optional()
              .nullable(),
            recurrenceType: z
              .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
              .optional(),
            recurrenceRule: z
              .object({
                frequency: z.enum(['UNKNOWN_FREQUENCY', 'WEEKLY']),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
                  )
                  .min(1)
                  .max(4)
                  .optional()
                  .nullable(),
                days: z
                  .array(
                    z.enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                  )
                  .min(1)
                  .max(1),
                until: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      ),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Date until when the event repeats.\nIf not specified, the event repeats forever.'
                  )
                  .optional(),
                adjustedUntil: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      ),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
                  )
                  .optional(),
              })
              .describe(
                "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
              )
              .optional(),
            recurringEventId: z
              .string()
              .describe(
                'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
              )
              .min(64)
              .max(64)
              .optional()
              .nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z.enum(['BUSINESS', 'CUSTOMER', 'CUSTOM']),
                name: z
                  .string()
                  .describe(
                    "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
                address: z
                  .string()
                  .describe(
                    "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
                  )
                  .min(1)
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .optional(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .optional()
                    .nullable(),
                  type: z
                    .string()
                    .describe('Resource type.')
                    .optional()
                    .nullable(),
                  transparency: z
                    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                    .optional(),
                  permissionRole: z.enum(['WRITER', 'COMMENTER']).optional(),
                })
              )
              .max(100)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .min(0)
              .optional()
              .nullable(),
            remainingCapacity: z
              .number()
              .int()
              .describe(
                'Number of participants who can still be added to the event.'
              )
              .optional()
              .nullable(),
            participants: z
              .object({
                total: z
                  .number()
                  .int()
                  .describe('Total number of participants.\n\nMin: `0`')
                  .min(0)
                  .optional()
                  .nullable(),
                list: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                        )
                        .min(1)
                        .max(200),
                      phone: z
                        .string()
                        .describe("Participant's phone number.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe("Participant's email address.")
                        .email()
                        .optional()
                        .nullable(),
                      contactId: z
                        .string()
                        .describe(
                          '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                hasMore: z
                  .boolean()
                  .describe(
                    "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
                  .optional(),
              })
              .describe(
                "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
              )
              .optional(),
            conferencingDetails: z
              .object({
                type: z.enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM']).optional(),
                hostUrl: z
                  .string()
                  .describe(
                    'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000),
                guestUrl: z
                  .string()
                  .describe(
                    'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000),
                password: z
                  .string()
                  .describe(
                    'Conference password.\n\nMin: 1 character\nMax: 100 characters'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                externalId: z
                  .string()
                  .describe(
                    'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
              )
              .optional(),
            notes: z
              .string()
              .describe(
                'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
              )
              .min(1)
              .max(5000)
              .optional()
              .nullable(),
            inheritedFields: z
              .array(
                z.enum([
                  'TITLE',
                  'TIME_ZONE',
                  'TIME',
                  'LOCATION',
                  'RESOURCES',
                  'CAPACITY',
                  'PARTICIPANTS',
                  'CONFERENCING_DETAILS',
                ])
              )
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            permissions: z
              .array(
                z.object({
                  identity: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ wixUserId: z.never().optional() }),
                        z.object({
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                    )
                    .optional(),
                  role: z.enum(['WRITER', 'COMMENTER']).optional(),
                })
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling applications or users to save custom data related to the event.'
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
          })
          .describe('Event to create.'),
      })
    )
    .min(1)
    .max(50),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return created events.\n\nDefault: `false`.')
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkCreateEventResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Event ID.\nShould always be available, unless it's impossible (for example, when failing to create an event)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array, for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this event.\nWhen `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Whether the requested action was successful.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Event ID.')
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            scheduleId: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalScheduleId: z
              .string()
              .describe(
                'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            scheduleName: z
              .string()
              .describe('Schedule name.')
              .optional()
              .nullable(),
            type: z
              .string()
              .describe(
                'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'CANCELLED'])
              .describe(
                'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
              )
              .optional(),
            title: z
              .string()
              .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            start: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information about when the event starts.')
              .optional(),
            end: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
              )
              .optional(),
            adjustedStart: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            adjustedEnd: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
              )
              .optional()
              .nullable(),
            recurrenceType: z
              .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
              .describe(
                "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
              )
              .optional(),
            recurrenceRule: z
              .object({
                frequency: z
                  .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
                  .describe(
                    "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
                  )
                  .min(1)
                  .max(4)
                  .optional()
                  .nullable(),
                days: z
                  .array(
                    z.enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                  )
                  .min(1)
                  .max(1)
                  .optional(),
                until: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Date until when the event repeats.\nIf not specified, the event repeats forever.'
                  )
                  .optional(),
                adjustedUntil: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
                  )
                  .optional(),
              })
              .describe(
                "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
              )
              .optional(),
            recurringEventId: z
              .string()
              .describe(
                'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
              )
              .min(64)
              .max(64)
              .optional()
              .nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z
                  .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
                  .describe(
                    "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
                  )
                  .optional(),
                name: z
                  .string()
                  .describe(
                    "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
                address: z
                  .string()
                  .describe(
                    "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
                  )
                  .min(1)
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .optional(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .optional()
                    .nullable(),
                  type: z
                    .string()
                    .describe('Resource type.')
                    .optional()
                    .nullable(),
                  transparency: z
                    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                    .describe(
                      "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                    )
                    .optional(),
                  permissionRole: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .min(0)
              .optional()
              .nullable(),
            remainingCapacity: z
              .number()
              .int()
              .describe(
                'Number of participants who can still be added to the event.'
              )
              .optional()
              .nullable(),
            participants: z
              .object({
                total: z
                  .number()
                  .int()
                  .describe('Total number of participants.\n\nMin: `0`')
                  .min(0)
                  .optional()
                  .nullable(),
                list: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                        )
                        .min(1)
                        .max(200)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Participant's phone number.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe("Participant's email address.")
                        .email()
                        .optional()
                        .nullable(),
                      contactId: z
                        .string()
                        .describe(
                          '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                hasMore: z
                  .boolean()
                  .describe(
                    "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
                  .describe(
                    "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
                  )
                  .optional(),
              })
              .describe(
                "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
              )
              .optional(),
            conferencingDetails: z
              .object({
                type: z
                  .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
                  .describe('Conference type. For example, `Zoom`.')
                  .optional(),
                hostUrl: z
                  .string()
                  .describe(
                    'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                guestUrl: z
                  .string()
                  .describe(
                    'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                password: z
                  .string()
                  .describe(
                    'Conference password.\n\nMin: 1 character\nMax: 100 characters'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                externalId: z
                  .string()
                  .describe(
                    'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
              )
              .optional(),
            notes: z
              .string()
              .describe(
                'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
              )
              .min(1)
              .max(5000)
              .optional()
              .nullable(),
            inheritedFields: z
              .array(
                z.enum([
                  'TITLE',
                  'TIME_ZONE',
                  'TIME',
                  'LOCATION',
                  'RESOURCES',
                  'CAPACITY',
                  'PARTICIPANTS',
                  'CONFERENCING_DETAILS',
                ])
              )
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            permissions: z
              .array(
                z.object({
                  identity: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ wixUserId: z.never().optional() }),
                        z.object({
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                    )
                    .optional(),
                  role: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling applications or users to save custom data related to the event.'
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
          })
          .describe('The event.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of events that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of events that couldn't be processed.")
        .optional(),
    })
    .describe('Total successes and failures.')
    .optional(),
});
export const UpdateEventRequest = z.object({
  _id: z.string().describe('Event ID.').min(36).max(250),
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z.enum(['CONFIRMED', 'CANCELLED']).optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.')
        .optional(),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        )
        .optional(),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            ),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z.enum(['UNKNOWN_FREQUENCY', 'WEEKLY']),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                ),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                ),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z.enum(['BUSINESS', 'CUSTOMER', 'CUSTOM']),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .optional(),
            permissionRole: z.enum(['WRITER', 'COMMENTER']).optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z.enum(['CONFIRMED', 'PENDING_CONFIRMATION']).optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z.enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM']).optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z.enum(['WRITER', 'COMMENTER']).optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        ),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Event to update.'),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to notify the participants about changes made to the schedule or event.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Message to send.\n\nMin: 1 character\nMax: 5000 characters'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.\n\nMax: 10 keys\nMax key length: 64 characters\nMax value length: 1000 characters'
            )
            .optional(),
        })
        .describe(
          'Information about whether participants of the updated event are notified and\nthe message they receive.'
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const UpdateEventResponse = z.object({
  _id: z.string().describe('Event ID.').min(36).max(250).optional().nullable(),
  scheduleId: z
    .string()
    .describe(
      "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  externalScheduleId: z
    .string()
    .describe(
      'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  scheduleName: z.string().describe('Schedule name.').optional().nullable(),
  type: z
    .string()
    .describe(
      'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['CONFIRMED', 'CANCELLED'])
    .describe(
      'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
    )
    .optional(),
  title: z
    .string()
    .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  start: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe('Information about when the event starts.')
    .optional(),
  end: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
    )
    .optional(),
  adjustedStart: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  adjustedEnd: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
    )
    .optional(),
  timeZone: z
    .string()
    .describe(
      'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
    )
    .optional()
    .nullable(),
  recurrenceType: z
    .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
    .describe(
      "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
    )
    .optional(),
  recurrenceRule: z
    .object({
      frequency: z
        .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
        .describe(
          "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
        )
        .optional(),
      interval: z
        .number()
        .int()
        .describe(
          'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
        )
        .min(1)
        .max(4)
        .optional()
        .nullable(),
      days: z
        .array(
          z.enum([
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY',
          ])
        )
        .min(1)
        .max(1)
        .optional(),
      until: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Date until when the event repeats.\nIf not specified, the event repeats forever.'
        )
        .optional(),
      adjustedUntil: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
    })
    .describe(
      "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
    )
    .optional(),
  recurringEventId: z
    .string()
    .describe(
      'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
    )
    .min(64)
    .max(64)
    .optional()
    .nullable(),
  transparency: z
    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
    .describe(
      'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
    )
    .optional(),
  location: z
    .object({
      _id: z
        .string()
        .describe(
          '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
        .describe(
          "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
        )
        .optional(),
      name: z
        .string()
        .describe(
          "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
      address: z
        .string()
        .describe(
          "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
        )
        .min(1)
        .max(300)
        .optional()
        .nullable(),
    })
    .describe(
      'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .optional(),
  resources: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Resource ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Resource name.').optional().nullable(),
        type: z.string().describe('Resource type.').optional().nullable(),
        transparency: z
          .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
          .describe(
            "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
          )
          .optional(),
        permissionRole: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
          )
          .optional(),
      })
    )
    .max(100)
    .optional(),
  totalCapacity: z
    .number()
    .int()
    .describe(
      'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
    )
    .min(0)
    .optional()
    .nullable(),
  remainingCapacity: z
    .number()
    .int()
    .describe('Number of participants who can still be added to the event.')
    .optional()
    .nullable(),
  participants: z
    .object({
      total: z
        .number()
        .int()
        .describe('Total number of participants.\n\nMin: `0`')
        .min(0)
        .optional()
        .nullable(),
      list: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                'Participant name.\n\nMin: 1 character\nMax: 200 characters'
              )
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Participant's phone number.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Participant's email address.")
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe(
                '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      hasMore: z
        .boolean()
        .describe(
          "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
        .describe(
          "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
        )
        .optional(),
    })
    .describe(
      "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
    )
    .optional(),
  conferencingDetails: z
    .object({
      type: z
        .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
        .describe('Conference type. For example, `Zoom`.')
        .optional(),
      hostUrl: z
        .string()
        .describe(
          'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      guestUrl: z
        .string()
        .describe(
          'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
        )
        .min(1)
        .max(2000)
        .optional()
        .nullable(),
      password: z
        .string()
        .describe(
          'Conference password.\n\nMin: 1 character\nMax: 100 characters'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      externalId: z
        .string()
        .describe(
          'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
        )
        .min(1)
        .max(150)
        .optional()
        .nullable(),
    })
    .describe(
      "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
    )
    .optional(),
  notes: z
    .string()
    .describe(
      'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
    )
    .min(1)
    .max(5000)
    .optional()
    .nullable(),
  inheritedFields: z
    .array(
      z.enum([
        'TITLE',
        'TIME_ZONE',
        'TIME',
        'LOCATION',
        'RESOURCES',
        'CAPACITY',
        'PARTICIPANTS',
        'CONFERENCING_DETAILS',
      ])
    )
    .optional(),
  appId: z
    .string()
    .describe(
      'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  permissions: z
    .array(
      z.object({
        identity: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ wixUserId: z.never().optional() }),
              z.object({
                wixUserId: z
                  .string()
                  .describe(
                    'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
          )
          .optional(),
        role: z
          .enum(['WRITER', 'COMMENTER'])
          .describe(
            'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
          )
          .optional(),
      })
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling applications or users to save custom data related to the event.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
});
export const BulkUpdateEventRequest = z.object({
  events: z
    .array(
      z.object({
        event: z
          .object({
            _id: z.string().describe('Event ID.').min(36).max(250),
            scheduleId: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalScheduleId: z
              .string()
              .describe(
                'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            scheduleName: z
              .string()
              .describe('Schedule name.')
              .optional()
              .nullable(),
            type: z
              .string()
              .describe(
                'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
              )
              .optional()
              .nullable(),
            status: z.enum(['CONFIRMED', 'CANCELLED']).optional(),
            title: z
              .string()
              .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            start: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information about when the event starts.')
              .optional(),
            end: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
              )
              .optional(),
            adjustedStart: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            adjustedEnd: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  ),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
              )
              .optional()
              .nullable(),
            recurrenceType: z
              .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
              .optional(),
            recurrenceRule: z
              .object({
                frequency: z.enum(['UNKNOWN_FREQUENCY', 'WEEKLY']),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
                  )
                  .min(1)
                  .max(4)
                  .optional()
                  .nullable(),
                days: z
                  .array(
                    z.enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                  )
                  .min(1)
                  .max(1),
                until: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      ),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Date until when the event repeats.\nIf not specified, the event repeats forever.'
                  )
                  .optional(),
                adjustedUntil: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      ),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
                  )
                  .optional(),
              })
              .describe(
                "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
              )
              .optional(),
            recurringEventId: z
              .string()
              .describe(
                'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
              )
              .min(64)
              .max(64)
              .optional()
              .nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z.enum(['BUSINESS', 'CUSTOMER', 'CUSTOM']),
                name: z
                  .string()
                  .describe(
                    "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
                address: z
                  .string()
                  .describe(
                    "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
                  )
                  .min(1)
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .optional(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .optional()
                    .nullable(),
                  type: z
                    .string()
                    .describe('Resource type.')
                    .optional()
                    .nullable(),
                  transparency: z
                    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                    .optional(),
                  permissionRole: z.enum(['WRITER', 'COMMENTER']).optional(),
                })
              )
              .max(100)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .min(0)
              .optional()
              .nullable(),
            remainingCapacity: z
              .number()
              .int()
              .describe(
                'Number of participants who can still be added to the event.'
              )
              .optional()
              .nullable(),
            participants: z
              .object({
                total: z
                  .number()
                  .int()
                  .describe('Total number of participants.\n\nMin: `0`')
                  .min(0)
                  .optional()
                  .nullable(),
                list: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                        )
                        .min(1)
                        .max(200),
                      phone: z
                        .string()
                        .describe("Participant's phone number.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe("Participant's email address.")
                        .email()
                        .optional()
                        .nullable(),
                      contactId: z
                        .string()
                        .describe(
                          '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                hasMore: z
                  .boolean()
                  .describe(
                    "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
                  .optional(),
              })
              .describe(
                "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
              )
              .optional(),
            conferencingDetails: z
              .object({
                type: z.enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM']).optional(),
                hostUrl: z
                  .string()
                  .describe(
                    'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000),
                guestUrl: z
                  .string()
                  .describe(
                    'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000),
                password: z
                  .string()
                  .describe(
                    'Conference password.\n\nMin: 1 character\nMax: 100 characters'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                externalId: z
                  .string()
                  .describe(
                    'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
              )
              .optional(),
            notes: z
              .string()
              .describe(
                'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
              )
              .min(1)
              .max(5000)
              .optional()
              .nullable(),
            inheritedFields: z
              .array(
                z.enum([
                  'TITLE',
                  'TIME_ZONE',
                  'TIME',
                  'LOCATION',
                  'RESOURCES',
                  'CAPACITY',
                  'PARTICIPANTS',
                  'CONFERENCING_DETAILS',
                ])
              )
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            permissions: z
              .array(
                z.object({
                  identity: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ wixUserId: z.never().optional() }),
                        z.object({
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                    )
                    .optional(),
                  role: z.enum(['WRITER', 'COMMENTER']).optional(),
                })
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling applications or users to save custom data related to the event.'
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
              ),
            _createdDate: z
              .date()
              .describe(
                'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
          })
          .describe('Event to update.'),
      })
    )
    .min(1)
    .max(50),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the updated events.\n\nDefault: `false`')
        .optional()
        .nullable(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to notify the participants about changes made to the schedule or event.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Message to send.\n\nMin: 1 character\nMax: 5000 characters'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.\n\nMax: 10 keys\nMax key length: 64 characters\nMax value length: 1000 characters'
            )
            .optional(),
        })
        .describe(
          'Information about whether participants of the updated event are notified and\nthe message they receive.'
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkUpdateEventResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Event ID.\nShould always be available, unless it's impossible (for example, when failing to create an event)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array, for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this event.\nWhen `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Whether the requested action was successful.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Event ID.')
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            scheduleId: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalScheduleId: z
              .string()
              .describe(
                'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            scheduleName: z
              .string()
              .describe('Schedule name.')
              .optional()
              .nullable(),
            type: z
              .string()
              .describe(
                'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'CANCELLED'])
              .describe(
                'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
              )
              .optional(),
            title: z
              .string()
              .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            start: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information about when the event starts.')
              .optional(),
            end: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
              )
              .optional(),
            adjustedStart: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            adjustedEnd: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
              )
              .optional()
              .nullable(),
            recurrenceType: z
              .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
              .describe(
                "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
              )
              .optional(),
            recurrenceRule: z
              .object({
                frequency: z
                  .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
                  .describe(
                    "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
                  )
                  .min(1)
                  .max(4)
                  .optional()
                  .nullable(),
                days: z
                  .array(
                    z.enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                  )
                  .min(1)
                  .max(1)
                  .optional(),
                until: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Date until when the event repeats.\nIf not specified, the event repeats forever.'
                  )
                  .optional(),
                adjustedUntil: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
                  )
                  .optional(),
              })
              .describe(
                "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
              )
              .optional(),
            recurringEventId: z
              .string()
              .describe(
                'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
              )
              .min(64)
              .max(64)
              .optional()
              .nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z
                  .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
                  .describe(
                    "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
                  )
                  .optional(),
                name: z
                  .string()
                  .describe(
                    "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
                address: z
                  .string()
                  .describe(
                    "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
                  )
                  .min(1)
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .optional(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .optional()
                    .nullable(),
                  type: z
                    .string()
                    .describe('Resource type.')
                    .optional()
                    .nullable(),
                  transparency: z
                    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                    .describe(
                      "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                    )
                    .optional(),
                  permissionRole: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .min(0)
              .optional()
              .nullable(),
            remainingCapacity: z
              .number()
              .int()
              .describe(
                'Number of participants who can still be added to the event.'
              )
              .optional()
              .nullable(),
            participants: z
              .object({
                total: z
                  .number()
                  .int()
                  .describe('Total number of participants.\n\nMin: `0`')
                  .min(0)
                  .optional()
                  .nullable(),
                list: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                        )
                        .min(1)
                        .max(200)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Participant's phone number.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe("Participant's email address.")
                        .email()
                        .optional()
                        .nullable(),
                      contactId: z
                        .string()
                        .describe(
                          '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                hasMore: z
                  .boolean()
                  .describe(
                    "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
                  .describe(
                    "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
                  )
                  .optional(),
              })
              .describe(
                "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
              )
              .optional(),
            conferencingDetails: z
              .object({
                type: z
                  .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
                  .describe('Conference type. For example, `Zoom`.')
                  .optional(),
                hostUrl: z
                  .string()
                  .describe(
                    'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                guestUrl: z
                  .string()
                  .describe(
                    'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                password: z
                  .string()
                  .describe(
                    'Conference password.\n\nMin: 1 character\nMax: 100 characters'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                externalId: z
                  .string()
                  .describe(
                    'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
              )
              .optional(),
            notes: z
              .string()
              .describe(
                'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
              )
              .min(1)
              .max(5000)
              .optional()
              .nullable(),
            inheritedFields: z
              .array(
                z.enum([
                  'TITLE',
                  'TIME_ZONE',
                  'TIME',
                  'LOCATION',
                  'RESOURCES',
                  'CAPACITY',
                  'PARTICIPANTS',
                  'CONFERENCING_DETAILS',
                ])
              )
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            permissions: z
              .array(
                z.object({
                  identity: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ wixUserId: z.never().optional() }),
                        z.object({
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                    )
                    .optional(),
                  role: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling applications or users to save custom data related to the event.'
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
          })
          .describe('The event.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of events that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of events that couldn't be processed.")
        .optional(),
    })
    .describe('Total successes and failures.')
    .optional(),
});
export const RestoreEventDefaultsRequest = z.object({
  eventId: z
    .string()
    .describe('ID of the event for which to restore default values.')
    .min(36)
    .max(250),
  fields: z
    .array(
      z.enum([
        'TITLE',
        'TIME_ZONE',
        'TIME',
        'LOCATION',
        'RESOURCES',
        'CAPACITY',
        'PARTICIPANTS',
        'CONFERENCING_DETAILS',
      ])
    )
    .min(1),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to notify the participants about changes made to the schedule or event.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Message to send.\n\nMin: 1 character\nMax: 5000 characters'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.\n\nMax: 10 keys\nMax key length: 64 characters\nMax value length: 1000 characters'
            )
            .optional(),
        })
        .describe(
          'Information about whether participants of the updated event are notified and\nthe message they receive.'
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const RestoreEventDefaultsResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'CANCELLED'])
        .describe(
          'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
        )
        .optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.')
        .optional(),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        )
        .optional(),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .describe(
          "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
        )
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z
            .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
            .describe(
              "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
            )
            .optional(),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1)
            .optional(),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .describe(
          'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
            .describe(
              "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
            )
            .optional(),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
              )
              .optional(),
            permissionRole: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z
            .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
            .describe(
              "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
            )
            .optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z
            .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
            .describe('Conference type. For example, `Zoom`.')
            .optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
              )
              .optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Updated event.')
    .optional(),
});
export const SplitRecurringEventRequest = z.object({
  recurringEventId: z
    .string()
    .describe('ID of the `MASTER` event to split.')
    .min(36)
    .max(250),
  splitLocalDate: z
    .object({ value: z.string().optional() })
    .describe(
      "Local date and time at which the `MASTER` event is split in\n[ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,\n`2025-03-11T09:00:00`.\nMust be a future date that's after the `start` date of the next `INSTANCE` or\n`EXCEPTION` event in the series. Additionally, there must be another\n`INSTANCE` or `EXCEPTION` event following this next event, as the changes\nwouldn't affect any event without a subsequent occurrence."
    ),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const SplitRecurringEventResponse = z.object({
  updatedRecurringEventEndingBeforeSplit: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'CANCELLED'])
        .describe(
          'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
        )
        .optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.')
        .optional(),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        )
        .optional(),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .describe(
          "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
        )
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z
            .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
            .describe(
              "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
            )
            .optional(),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1)
            .optional(),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .describe(
          'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
            .describe(
              "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
            )
            .optional(),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
              )
              .optional(),
            permissionRole: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z
            .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
            .describe(
              "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
            )
            .optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z
            .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
            .describe('Conference type. For example, `Zoom`.')
            .optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
              )
              .optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Original `MASTER` event that was shortened.')
    .optional(),
  newRecurringEventStartingFromSplit: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'CANCELLED'])
        .describe(
          'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
        )
        .optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.')
        .optional(),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        )
        .optional(),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .describe(
          "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
        )
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z
            .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
            .describe(
              "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
            )
            .optional(),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1)
            .optional(),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .describe(
          'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
            .describe(
              "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
            )
            .optional(),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
              )
              .optional(),
            permissionRole: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z
            .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
            .describe(
              "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
            )
            .optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z
            .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
            .describe('Conference type. For example, `Zoom`.')
            .optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
              )
              .optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'New `MASTER` event starting with the first event after the split date.'
    )
    .optional(),
});
export const CancelEventRequest = z.object({
  eventId: z.string().describe('ID of the event to cancel.').min(36).max(250),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to notify the participants about changes made to the schedule or event.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Message to send.\n\nMin: 1 character\nMax: 5000 characters'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.\n\nMax: 10 keys\nMax key length: 64 characters\nMax value length: 1000 characters'
            )
            .optional(),
        })
        .describe(
          'Information about whether participants of the canceled event are notified and\nthe message they receive.'
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CancelEventResponse = z.object({
  event: z
    .object({
      _id: z
        .string()
        .describe('Event ID.')
        .min(36)
        .max(250)
        .optional()
        .nullable(),
      scheduleId: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalScheduleId: z
        .string()
        .describe(
          'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      scheduleName: z.string().describe('Schedule name.').optional().nullable(),
      type: z
        .string()
        .describe(
          'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
        )
        .optional()
        .nullable(),
      status: z
        .enum(['CONFIRMED', 'CANCELLED'])
        .describe(
          'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
        )
        .optional(),
      title: z
        .string()
        .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      start: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about when the event starts.')
        .optional(),
      end: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
        )
        .optional(),
      adjustedStart: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      adjustedEnd: z
        .object({
          localDate: z
            .string()
            .describe(
              'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
            )
            .optional()
            .nullable(),
          timeZone: z
            .string()
            .describe(
              'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
            )
            .optional()
            .nullable(),
          utcDate: z
            .date()
            .describe(
              'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
        )
        .optional()
        .nullable(),
      recurrenceType: z
        .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
        .describe(
          "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
        )
        .optional(),
      recurrenceRule: z
        .object({
          frequency: z
            .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
            .describe(
              "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
            )
            .optional(),
          interval: z
            .number()
            .int()
            .describe(
              'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
            )
            .min(1)
            .max(4)
            .optional()
            .nullable(),
          days: z
            .array(
              z.enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
            )
            .min(1)
            .max(1)
            .optional(),
          until: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Date until when the event repeats.\nIf not specified, the event repeats forever.'
            )
            .optional(),
          adjustedUntil: z
            .object({
              localDate: z
                .string()
                .describe(
                  'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                )
                .optional()
                .nullable(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                )
                .optional()
                .nullable(),
              utcDate: z
                .date()
                .describe(
                  'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
            )
            .optional(),
        })
        .describe(
          "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
        )
        .optional(),
      recurringEventId: z
        .string()
        .describe(
          'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
        )
        .min(64)
        .max(64)
        .optional()
        .nullable(),
      transparency: z
        .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
        .describe(
          'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
        )
        .optional(),
      location: z
        .object({
          _id: z
            .string()
            .describe(
              '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
            .describe(
              "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
            )
            .optional(),
          name: z
            .string()
            .describe(
              "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
          address: z
            .string()
            .describe(
              "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
            )
            .min(1)
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .optional(),
      resources: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Resource ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z.string().describe('Resource name.').optional().nullable(),
            type: z.string().describe('Resource type.').optional().nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
              )
              .optional(),
            permissionRole: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
        )
        .min(0)
        .optional()
        .nullable(),
      remainingCapacity: z
        .number()
        .int()
        .describe('Number of participants who can still be added to the event.')
        .optional()
        .nullable(),
      participants: z
        .object({
          total: z
            .number()
            .int()
            .describe('Total number of participants.\n\nMin: `0`')
            .min(0)
            .optional()
            .nullable(),
          list: z
            .array(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                  )
                  .min(1)
                  .max(200)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe("Participant's phone number.")
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe("Participant's email address.")
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe(
                    '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
          hasMore: z
            .boolean()
            .describe(
              "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
            )
            .optional()
            .nullable(),
          status: z
            .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
            .describe(
              "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
            )
            .optional(),
        })
        .describe(
          "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
        )
        .optional(),
      conferencingDetails: z
        .object({
          type: z
            .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
            .describe('Conference type. For example, `Zoom`.')
            .optional(),
          hostUrl: z
            .string()
            .describe(
              'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          guestUrl: z
            .string()
            .describe(
              'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
            )
            .min(1)
            .max(2000)
            .optional()
            .nullable(),
          password: z
            .string()
            .describe(
              'Conference password.\n\nMin: 1 character\nMax: 100 characters'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          externalId: z
            .string()
            .describe(
              'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
        )
        .optional(),
      notes: z
        .string()
        .describe(
          'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
        )
        .min(1)
        .max(5000)
        .optional()
        .nullable(),
      inheritedFields: z
        .array(
          z.enum([
            'TITLE',
            'TIME_ZONE',
            'TIME',
            'LOCATION',
            'RESOURCES',
            'CAPACITY',
            'PARTICIPANTS',
            'CONFERENCING_DETAILS',
          ])
        )
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      permissions: z
        .array(
          z.object({
            identity: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ wixUserId: z.never().optional() }),
                  z.object({
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
              )
              .optional(),
            role: z
              .enum(['WRITER', 'COMMENTER'])
              .describe(
                'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
              )
              .optional(),
          })
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling applications or users to save custom data related to the event.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Canceled event.')
    .optional(),
});
export const BulkCancelEventRequest = z.object({
  eventIds: z.array(z.string()).min(1).max(50),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the canceled events.\n\nDefault: `false`')
        .optional()
        .nullable(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to notify the participants about changes made to the schedule or event.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Message to send.\n\nMin: 1 character\nMax: 5000 characters'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
          metadata: z
            .record(z.string(), z.string())
            .describe(
              'Information about the delivery channels used to send the notification.\nFor example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.\n\nMax: 10 keys\nMax key length: 64 characters\nMax value length: 1000 characters'
            )
            .optional(),
        })
        .describe(
          'Information about whether participants of the canceled events are notified and\nthe message they receive.'
        )
        .optional(),
      timeZone: z
        .string()
        .describe(
          'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)\nfor calculating `adjustedStart` and `adjustedEnd`. For example,\n`America/New_York` or `UTC`.\n\nDefault: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkCancelEventResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Event ID.\nShould always be available, unless it's impossible (for example, when failing to create an event)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array, for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this event.\nWhen `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Whether the requested action was successful.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Event ID.')
              .min(36)
              .max(250)
              .optional()
              .nullable(),
            scheduleId: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nthe event belongs to.\nAfter creating an event, you can't assign it to a different schedule."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalScheduleId: z
              .string()
              .describe(
                'ID of the external schedule the event belongs to.\n\nFor example, if the event belongs to a Bookings staff member, identical to\nthe ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members-and-resources/resource-v2/resource-object).\nIf the schedule belongs to a Bookings service,\nidentical to the ID of the [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/service-object).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            scheduleName: z
              .string()
              .describe('Schedule name.')
              .optional()
              .nullable(),
            type: z
              .string()
              .describe(
                'Event type. You can set the event type but you can\'t update it.\n\nSupported values:\n+ `DEFAULT`: A standard event that\'s not further specified.\n+ `WORKING_HOURS`: The event adds working hours to a schedule.\nBy default not returned in [Query Events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/query-events).\nTo retrieve working hour events, specify `"type": "WORKING_HOURS"` in the query filter.\nAdditional supported values, if Wix Bookings is installed:\n+ `APPOINTMENT`: Bookings appointment event.\n+ `CLASS`: Bookings class event.\n+ `COURSE`: Bookings course event.'
              )
              .optional()
              .nullable(),
            status: z
              .enum(['CONFIRMED', 'CANCELLED'])
              .describe(
                'Event status.\n\nSupported values:\n+ `CONFIRMED`: Event is scheduled to happen or has happened.\n+ `CANCELLED`: Event has been canceled.\n\nDefault: `CONFIRMED`'
              )
              .optional(),
            title: z
              .string()
              .describe('Event title.\n\nMin: 1 character\nMax: 200 characters')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            start: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information about when the event starts.')
              .optional(),
            end: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends.\n\nMaximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first)."
              )
              .optional(),
            adjustedStart: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event starts adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            adjustedEnd: z
              .object({
                localDate: z
                  .string()
                  .describe(
                    'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                  )
                  .optional()
                  .nullable(),
                timeZone: z
                  .string()
                  .describe(
                    'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                  )
                  .optional()
                  .nullable(),
                utcDate: z
                  .date()
                  .describe(
                    'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about when the event ends adjusted to the `timeZone` of the\nbusiness [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
              )
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone the event is associated with in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nOnly regional time zones and UTC are supported.\nFor example, `America/New_York` or `UTC`.\n\nDefault: `timeZone` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object).'
              )
              .optional()
              .nullable(),
            recurrenceType: z
              .enum(['NONE', 'MASTER', 'INSTANCE', 'EXCEPTION'])
              .describe(
                "Information about whether the event is a single occurrence or part of a\nrecurring series.\n\nSupported values:\n+ `NONE`: The event occurs only once and doesn't repeat.\n+ `MASTER`: Defines the recurrence pattern for a series of recurring events.\n+ `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{\"recurrenceType\": \"INSTANCE\"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.\n+ `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.\n\nDefault: `NONE`"
              )
              .optional(),
            recurrenceRule: z
              .object({
                frequency: z
                  .enum(['UNKNOWN_FREQUENCY', 'WEEKLY'])
                  .describe(
                    "Frequency how often the event repeats. Works together with `interval`.\n\nSupported values:\n+ `WEEKLY`: The event's recurrence pattern is based on weeks."
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval how often the event repeats. Works together with `frequency`.\nFor example, `frequency` set to `WEEKLY`  and `interval` set to `2` means\nthe event repeats every 2 weeks.\n\nMin: `1`\nMax: `4`\nDefault: `1`'
                  )
                  .min(1)
                  .max(4)
                  .optional()
                  .nullable(),
                days: z
                  .array(
                    z.enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                  )
                  .min(1)
                  .max(1)
                  .optional(),
                until: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Date until when the event repeats.\nIf not specified, the event repeats forever.'
                  )
                  .optional(),
                adjustedUntil: z
                  .object({
                    localDate: z
                      .string()
                      .describe(
                        'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
                      )
                      .optional()
                      .nullable(),
                    timeZone: z
                      .string()
                      .describe(
                        'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
                      )
                      .optional()
                      .nullable(),
                    utcDate: z
                      .date()
                      .describe(
                        'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Date until when the event repeats\nadjusted to the `timeZone` of the business\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor a different `timeZone` you provide in the call's request."
                  )
                  .optional(),
              })
              .describe(
                "Recurrence pattern for a series of events. This field is required when\ncreating a `MASTER` event and isn't available for non-recurring events.\nYou can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.\n\nFor example, an event that repeats every second Monday until January 7, 2026,\nat 8:00 AM has the following `recurrenceRule`:\n- `frequency = WEEKLY`\n- `interval = 2`\n- `days = [MONDAY]`\n- `until = 20260107T08:00:00Z`"
              )
              .optional(),
            recurringEventId: z
              .string()
              .describe(
                'ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`\nand `EXCEPTION` events.'
              )
              .min(64)
              .max(64)
              .optional()
              .nullable(),
            transparency: z
              .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
              .describe(
                'Specifies whether the event blocks time in the schedule it belongs to.\n\nSupported values:\n+ `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`'
              )
              .optional(),
            location: z
              .object({
                _id: z
                  .string()
                  .describe(
                    '[Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\nID. Available only for `BUSINESS` locations.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z
                  .enum(['BUSINESS', 'CUSTOMER', 'CUSTOM'])
                  .describe(
                    "Location type.\n\nSupported values:\n+ `BUSINESS`: The event is held at a business location.\n+ `CUSTOMER`: The event is held at the customer's location, such as their home or office.\n+ `CUSTOM`: The event is held at an address or venue not tied to the business or customer."
                  )
                  .optional(),
                name: z
                  .string()
                  .describe(
                    "Location name. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)\n`name`.\n\nMin: 1 character\nMax: 150 characters"
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
                address: z
                  .string()
                  .describe(
                    "Location address. For `BUSINESS` locations, it's identical to the\n[location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s\n`formattedAddress`."
                  )
                  .min(1)
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event location.\n\nDefault: `defaultLocation` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .optional(),
            resources: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Resource ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Resource name.')
                    .optional()
                    .nullable(),
                  type: z
                    .string()
                    .describe('Resource type.')
                    .optional()
                    .nullable(),
                  transparency: z
                    .enum(['UNKNOWN_TRANSPARENCY', 'OPAQUE', 'TRANSPARENT'])
                    .describe(
                      "Specifies whether the event blocks time in the resource's schedule.\n\nSupported values:\n+ `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.\n+ `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.\n\nDefault: `OPAQUE`."
                    )
                    .optional(),
                  permissionRole: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role associated with the resource. Refer to the\n[permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)\nfor more information.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Maximum number of participants who can participate in the event.\n\nDefault: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to'
              )
              .min(0)
              .optional()
              .nullable(),
            remainingCapacity: z
              .number()
              .int()
              .describe(
                'Number of participants who can still be added to the event.'
              )
              .optional()
              .nullable(),
            participants: z
              .object({
                total: z
                  .number()
                  .int()
                  .describe('Total number of participants.\n\nMin: `0`')
                  .min(0)
                  .optional()
                  .nullable(),
                list: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Participant name.\n\nMin: 1 character\nMax: 200 characters'
                        )
                        .min(1)
                        .max(200)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Participant's phone number.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe("Participant's email address.")
                        .email()
                        .optional()
                        .nullable(),
                      contactId: z
                        .string()
                        .describe(
                          '[Contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nID of the participant.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                hasMore: z
                  .boolean()
                  .describe(
                    "Whether there are more participants for the event than listed.\n\n+ `false`: The list includes all participants.\n+ `true`: The list doesn't include all participants."
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['CONFIRMED', 'PENDING_CONFIRMATION'])
                  .describe(
                    "Participant's status.\n\nSupported values:\n+ `CONFIRMED`: All participants are confirmed.\n+ `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet."
                  )
                  .optional(),
              })
              .describe(
                "Information about the event's participants.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information)."
              )
              .optional(),
            conferencingDetails: z
              .object({
                type: z
                  .enum(['UNKNOWN_TYPE', 'ZOOM', 'CUSTOM'])
                  .describe('Conference type. For example, `Zoom`.')
                  .optional(),
                hostUrl: z
                  .string()
                  .describe(
                    'URL used by the host to start the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                guestUrl: z
                  .string()
                  .describe(
                    'URL used by a guest to join the conference.\n\nMin: 1 character\nMax: 2000 characters'
                  )
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                password: z
                  .string()
                  .describe(
                    'Conference password.\n\nMin: 1 character\nMax: 100 characters'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                externalId: z
                  .string()
                  .describe(
                    'Conference ID in an external system.\n\nMin: 1 character\nMax: 150 characters'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Information about the event's online conferencing.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nDefault: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object) the event belongs to"
              )
              .optional(),
            notes: z
              .string()
              .describe(
                'Additional notes about the event.\nReturned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#retrieve-personal-information).\n\nMin: 1 character\nMax: 5000 characters'
              )
              .min(1)
              .max(5000)
              .optional()
              .nullable(),
            inheritedFields: z
              .array(
                z.enum([
                  'TITLE',
                  'TIME_ZONE',
                  'TIME',
                  'LOCATION',
                  'RESOURCES',
                  'CAPACITY',
                  'PARTICIPANTS',
                  'CONFERENCING_DETAILS',
                ])
              )
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app that owns the event. Identical to `appId` of the\n[schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the event belongs.\nFor events from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            permissions: z
              .array(
                z.object({
                  identity: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ wixUserId: z.never().optional() }),
                        z.object({
                          wixUserId: z
                            .string()
                            .describe(
                              'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      '[Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)\ngranted the permission.'
                    )
                    .optional(),
                  role: z
                    .enum(['WRITER', 'COMMENTER'])
                    .describe(
                      'Permission role.\n\nSupported values:\n+ `WRITER`: Full read and write access.\n+ `COMMENTER`: Full read access, write access limited to `event.notes`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling applications or users to save custom data related to the event.'
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments each time the event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the event.\nIgnored when creating an event.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
          })
          .describe('The event.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of events that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of events that couldn't be processed.")
        .optional(),
    })
    .describe('Total successes and failures.')
    .optional(),
});
